/**
 * Shared CSV → transactions parser (frontend copy).
 *
 * Canonical logic mirrors backend/app/services/parseTransactionsCsv.js — keep the
 * two in sync. Used by the Accounts CSV upload dialog so a manual upload and a
 * local-agent import normalize identically.
 *
 * Output sign convention: negative = money out (spend), positive = money in.
 */

export interface ParsedCsvTransaction {
  date: string; // YYYY-MM-DD
  amount: number;
  name: string;
}

const DATE_HEADERS = ["date", "transaction date", "posting date"];
const AMOUNT_HEADERS = ["amount", "debit", "credit"];
const NAME_HEADERS = ["description", "name", "memo", "merchant"];

export function parseTransactionsCsv(text: string): ParsedCsvTransaction[] {
  if (!text || typeof text !== "string") return [];
  const lines = text.trim().split("\n");
  if (lines.length < 2) return [];

  const header = lines[0].toLowerCase();
  const headerCols = header.split(",").map((h) => h.trim().replace(/"/g, ""));

  const dateIdx = headerCols.findIndex((h) => DATE_HEADERS.includes(h));
  const amountIdx = headerCols.findIndex((h) => AMOUNT_HEADERS.includes(h));
  const nameIdx = headerCols.findIndex((h) => NAME_HEADERS.includes(h));

  if (dateIdx === -1 || amountIdx === -1 || nameIdx === -1) {
    throw new Error("CSV must have Date, Amount, and Description columns");
  }

  const amountHeaderRaw = headerCols[amountIdx] || "amount";
  const amountHeader = amountHeaderRaw.toLowerCase();
  const amountKind = amountHeader.includes("credit")
    ? "credit"
    : amountHeader.includes("debit")
      ? "debit"
      : "amount";

  const transactions: ParsedCsvTransaction[] = [];
  let positiveCount = 0;
  let negativeCount = 0;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const cols = line.split(",").map((c) => c.trim().replace(/"/g, ""));
    const dateStr = cols[dateIdx];
    const amountStr = cols[amountIdx];
    const name = cols[nameIdx];
    if (!dateStr || !amountStr || !name) continue;

    let date = dateStr;
    if (dateStr.includes("/")) {
      const parts = dateStr.split("/");
      if (parts.length === 3) {
        const [m, d, y] = parts;
        const year = y.length === 2 ? `20${y}` : y;
        date = `${year}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      }
    }

    const cleanedAmount = amountStr.replace(/[$,]/g, "").trim();
    const normalizedAmountStr =
      cleanedAmount.startsWith("(") && cleanedAmount.endsWith(")")
        ? `-${cleanedAmount.slice(1, -1)}`
        : cleanedAmount;
    const amount = parseFloat(normalizedAmountStr);
    if (Number.isNaN(amount)) continue;
    if (amount > 0) positiveCount += 1;
    if (amount < 0) negativeCount += 1;

    transactions.push({ date, amount, name });
  }

  const totalCount = positiveCount + negativeCount;
  const shouldFlipAmountColumn =
    amountKind === "amount" &&
    totalCount > 0 &&
    negativeCount / totalCount >= 0.6;

  const normalized = transactions.map((txn) => {
    let normalizedAmount = txn.amount;
    if (amountKind === "credit") {
      normalizedAmount = -Math.abs(normalizedAmount);
    } else if (amountKind === "debit") {
      normalizedAmount = Math.abs(normalizedAmount);
    } else if (shouldFlipAmountColumn) {
      normalizedAmount = -normalizedAmount;
    }
    return { ...txn, amount: normalizedAmount };
  });

  console.log("[DEBUG] CSV import normalization", {
    amountHeader: amountHeaderRaw,
    amountKind,
    positiveCount,
    negativeCount,
    flippedAmountColumn: shouldFlipAmountColumn,
    sample: normalized.slice(0, 3),
  });

  return normalized;
}
